/* eslint-disable react-hooks/exhaustive-deps */
import { useForm } from "react-hook-form";
import {
  ControlledInput,
  ControlledSelect,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
  TagInput,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useNavigate } from "react-router";
import { FLOAT_REGEX, numbersOnly } from "../../utils/regexValidations";
import {
  buildRewardPayload,
  useMarketPricing,
} from "../../store/useMarketPricing";
import { useEffect, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import { withAdminPermission } from "../../utils/HOC/withAdminPermission";
import { useAuth } from "../../store/useAuth";
import { usePartners } from "../../store/usePartners";
import { useDropLocations } from "../../store/useDropLocations";
import {
  ILocationRateRow,
  toLocationRates,
  validateLocationRateRows,
} from "../../utils/locationRates";
import { LocationRatesEditor } from "./LocationRatesEditor";

type Inputs = {
  name: string;
  measurement: string;
  rate: number;
  co2_rate: number;
  droppie_revenue_kg?: number;
  droppie_revenue_item?: number;
  droppie_revenue_month?: number;
  kgConversion: number;
  maxAllowed: number;
  cbs: number;
  dropType?: string;
  rewardType?: string;
  voucherRewardPartnerId?: string;
  order?: number;
};

function AddMarketPricingScreen() {
  /** Hooks */
  const { addMarketPricing, addedMarketPricing, clearAddedMarketPricing } =
    useMarketPricing();
  const { user } = useAuth();
  const { setAlert } = useAlert();
  const { uploadImage, clearUploadedImage, uploadedImage } = useUploadImage();
  const { allPartners, getPartnersForSelect } = usePartners();
  const { dropLocations, getDropLocations } = useDropLocations();
  const navigate = useNavigate();
  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [keywords, setKeywords] = useState<string[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const isSuperAdmin = Number(user?.user_role) === 7;
  const [locationRateRows, setLocationRateRows] = useState<ILocationRateRow[]>(
    []
  );
  const [showLocationRateErrors, setShowLocationRateErrors] = useState(false);
  const locationRateErrors = validateLocationRateRows(locationRateRows);

  /** Functions */
  const goBack = () => navigate(-1);
  const onSubmit = (data: Inputs) => {
    if (!image) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    // Only super admins may set location rates (the backend refuses them
    // from anyone else); voucher streams get none, which the backend also
    // enforces.
    const sendLocationRates =
      isSuperAdmin &&
      data?.rewardType !== "PARTNER_VOUCHER" &&
      locationRateRows.length > 0;
    if (sendLocationRates && locationRateErrors.some(Boolean)) {
      return setShowLocationRateErrors(true);
    }
    const { voucherRewardPartnerId: _vp, ...formData } = data;
    const payload: Record<string, unknown> = {
      ...formData,
      type: data?.name?.toLowerCase(),
      image: uploadedImage,
      keywords,
      // Voucher streams have no consumer payout: rate 0, which the backend
      // also enforces when it creates a voucher stream.
      rate: data?.rewardType === "PARTNER_VOUCHER" ? 0 : data?.rate,
      ...buildRewardPayload(data),
    };
    // Left blank means "no opinion" — drop the key so the model's own default
    // applies instead of sending an empty string the backend would reject.
    if (data.order === undefined || String(data.order).trim() === "") {
      delete payload.order;
    } else {
      payload.order = Number(data.order);
    }
    if (sendLocationRates) {
      payload.locationRates = toLocationRates(locationRateRows);
    }
    if (!isSuperAdmin) {
      // The backend refuses these from anyone but a super admin, and the
      // reward fields are always in the payload, so they are left out here.
      const { maxAllowed: _, rewardType: _rt, voucherReward: _vr, ...safePayload } = payload;
      addMarketPricing(safePayload as any);
      return;
    }
    addMarketPricing(payload as any);
  };

  useEffect(() => {
    getPartnersForSelect();
    getDropLocations();
    return () => {
      clearUploadedImage();
    };
  }, []);

  // Voucher streams have no consumer payout: the rate input is hidden and the
  // value pinned to 0 so the (possibly retained) required/pattern rules pass.
  const rewardTypeValue = watch("rewardType");
  useEffect(() => {
    if (rewardTypeValue === "PARTNER_VOUCHER") {
      setValue("rate", 0, { shouldValidate: true });
    }
  }, [rewardTypeValue, setValue]);

  useEffect(() => {
    if (addedMarketPricing) {
      clearAddedMarketPricing();
      goBack();
    }
  }, [addedMarketPricing]);

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const imageError = submitted && !image;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex w-1/2 justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          Stream economics options
        </h1>
      </div>
      <div className=" w-full sm:w-1/2 bg-white rounded-xl drop-shadow-none">
        <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
          <p className="font-satoshi font-medium text-base text-dark">
            Add stream option
          </p>
        </div>
        <div className="w-full px-7 py-7">
          <ControlledInput
            name="name"
            control={control}
            label="Name"
            placeholder="Add name"
            type="text"
            rules={{ required: true }}
            error={Boolean(errors?.name)}
          />
          <ControlledInput
            name="order"
            control={control}
            label="List order (lower shows first in the app)"
            placeholder="e.g. 10"
            type="text"
            rules={{ required: false, pattern: numbersOnly }}
            error={Boolean(
              // @ts-ignore
              errors?.order || !numbersOnly.test(watch("order") ?? "")
            )}
          />
          <ControlledSelect
            name="measurement"
            control={control}
            rules={{ required: true }}
            error={Boolean(errors?.measurement)}
            label="Measurement type"
            options={[
              { _id: "kg", title: "Kg" },
              { _id: "unit", title: "Item" },
              { _id: "statiegeld", title: "Statiegeld" },
            ]}
            placeholder="Pick measurement type here"
          />
          <ControlledSelect
            name="dropType"
            control={control}
            rules={{ required: false }}
            error={Boolean(errors?.dropType)}
            label="Drop type"
            options={[
              { _id: "default", title: "Default" },
              { _id: "statiegeld", title: "Statiegeld" },
            ]}
            placeholder="Pick drop type here"
          />
          <ControlledSelect
            name="rewardType"
            control={control}
            rules={{ required: false }}
            error={Boolean(errors?.rewardType)}
            label="Reward type"
            options={[
              { _id: "CASH", title: "Cash (balance credit)" },
              { _id: "PARTNER_VOUCHER", title: "Partner voucher" },
            ]}
            placeholder="Cash (balance credit)"
          />
          {watch("rewardType") === "PARTNER_VOUCHER" ? (
            <ControlledSelect
              name="voucherRewardPartnerId"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.voucherRewardPartnerId)}
              label="Voucher partner"
              options={allPartners.map((partner) => ({
                _id: partner._id as string,
                title: partner.name as string,
              }))}
              placeholder="Pick the partner whose vouchers this stream issues"
            />
          ) : null}
          {watch("rewardType") !== "PARTNER_VOUCHER" ? (
            <ControlledInput
              name="rate"
              control={control}
              label="Consumer payout rate"
              placeholder="Add rate"
              type="text"
              rules={{ required: true, pattern: FLOAT_REGEX }}
              error={Boolean(
                // @ts-ignore
                errors?.rate || !FLOAT_REGEX.test(watch("rate") ?? 0)
              )}
            />
          ) : null}
          <ControlledInput
            name="droppie_revenue_kg"
            control={control}
            label="Droppie revenue (kg)"
            placeholder="Add Droppie revenue per kg"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.droppie_revenue_kg ||
              // @ts-ignore
              !FLOAT_REGEX.test(watch("droppie_revenue_kg") ?? 0)
            )}
          />
          {isSuperAdmin && watch("rewardType") !== "PARTNER_VOUCHER" ? (
            <LocationRatesEditor
              defaultRevenue={watch("droppie_revenue_kg")}
              rows={locationRateRows}
              onChange={setLocationRateRows}
              locations={dropLocations ?? []}
              errors={locationRateErrors}
              showErrors={showLocationRateErrors}
            />
          ) : null}
          <ControlledInput
            name="droppie_revenue_item"
            control={control}
            label="Droppie revenue (item)"
            placeholder="Add Droppie revenue per item"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.droppie_revenue_item ||
              // @ts-ignore
              !FLOAT_REGEX.test(watch("droppie_revenue_item") ?? 0)
            )}
          />
          <ControlledInput
            name="droppie_revenue_month"
            control={control}
            label="Droppie revenue (month)"
            placeholder="Add Droppie revenue per month"
            type="text"
            rules={{ required: false, pattern: FLOAT_REGEX }}
            error={Boolean(
              errors?.droppie_revenue_month ||
              // @ts-ignore
              !FLOAT_REGEX.test(watch("droppie_revenue_month") ?? 0)
            )}
          />
          <ControlledInput
            name="maxAllowed"
            control={control}
            label="Max (kg/items) Allowed"
            placeholder="Add Max Allowed"
            type="text"
            rules={
              isSuperAdmin
                ? { required: true, pattern: FLOAT_REGEX }
                : { required: false, pattern: FLOAT_REGEX }
            }
            disabled={!isSuperAdmin}
            error={Boolean(
              // @ts-ignore
              errors?.maxAllowed || !FLOAT_REGEX.test(watch("maxAllowed") ?? 0)
            )}
          />
          <ControlledInput
            name="co2_rate"
            control={control}
            label="CO2 Rate"
            placeholder="Add CO2 Rate"
            type="text"
            rules={{ required: true, pattern: FLOAT_REGEX }}
            error={Boolean(
              // @ts-ignore
              errors?.co2_rate || !FLOAT_REGEX.test(watch("co2_rate") ?? 0)
            )}
          />
          {watch("measurement") === "unit" ? (
            <ControlledInput
              name="kgConversion"
              control={control}
              label="KG Conversion"
              placeholder="Add kg conversion per item"
              type="text"
              rules={{ required: true, pattern: FLOAT_REGEX }}
              error={Boolean(
                errors?.kgConversion ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("kgConversion") ?? 0)
              )}
            />
          ) : null}
          <ControlledInput
            name="cbs"
            control={control}
            label="CBS Data"
            placeholder="Add CBS data"
            type="text"
            rules={{ required: true, pattern: FLOAT_REGEX }}
            error={Boolean(
              // @ts-ignore
              errors?.cbs || !FLOAT_REGEX.test(watch("co2_rate") ?? 0)
            )}
          />
          <TagInput
            label="Keywords"
            keywords={keywords}
            setKeywords={setKeywords}
          />
          <FormUpload
            label="Field icon"
            onChange={onImageChange}
            value={image}
            error={imageError}
          />
        </div>
      </div>
      <div className="w-1/2 flex flex-row items-center justify-end mt-[29px]">
        <SecondaryButton
          type="button"
          onClick={goBack}
          label="Cancel"
          className="mr-3 h-8 px-4 py-0 bg-white border border-droppie-oranje text-droppie-oranje hover:bg-droppie-oranje/5 flex items-center justify-center"
          labelClassName="text-droppie-oranje"
        />
        <PrimaryButton
          type="submit"
          size="sm"
          className="h-10 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          label="Save"
        />
      </div>
    </form>
  );
}

export default withProtectedRoute(withAdminPermission(AddMarketPricingScreen));
